/**
 * Admin Dashboard
 * Superuser control center: platform stats, login map, activity feed,
 * user management, and school management.
 */
import React, { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import Mascot from '../../components/shared/Mascot';
import {
  getAllSchoolsWithStats,
  createSchool,
  updateSchool,
  deleteSchool,
  SchoolStatsResponse,
  SchoolCreateRequest,
  SchoolUpdateRequest,
} from '../../api/schools';
import {
  getAdminStats,
  getAdminActivity,
  getAdminLoginMap,
  getAdminUsers,
  updateAdminUser,
  adminResetPassword,
  adminDeleteUser,
  AdminStats,
  ActivityEvent,
  LoginMap,
  AdminUser,
} from '../../api/courses';

type TabKey = 'overview' | 'activity' | 'users' | 'schools';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'activity', label: 'Activity' },
  { key: 'users', label: 'Users' },
  { key: 'schools', label: 'Schools' },
];

/** Relative-ish time for compact lists. */
const timeAgo = (iso: string): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'never';
  const s = (Date.now() - d.getTime()) / 1000;
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
};

const serverDetail = (err: any, fallback: string): string =>
  err?.response?.data?.detail || fallback;

/** Plain metric card: quiet label, big number, small tinted icon square. */
const StatCard: React.FC<{ label: string; value: number; sub?: string; tint: string; icon: React.ReactNode }> = ({
  label,
  value,
  sub,
  tint,
  icon,
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</p>
        <p className="mt-1 text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">
          {value.toLocaleString()}
        </p>
        {sub && <p className="mt-0.5 truncate text-xs font-medium text-gray-500 dark:text-gray-400">{sub}</p>}
      </div>
      <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${tint}`}>{icon}</span>
    </div>
  </div>
);

const Spinner: React.FC = () => (
  <div className="flex justify-center py-12">
    <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
  </div>
);

const AdminDashboard: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('overview');

  // ===== Overview state =====
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');
  const [mapDays, setMapDays] = useState(30);
  const [loginMap, setLoginMap] = useState<LoginMap | null>(null);
  const [mapLoading, setMapLoading] = useState(true);

  // ===== Activity state =====
  const [activity, setActivity] = useState<ActivityEvent[]>([]);
  const [activityLoaded, setActivityLoaded] = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityUser, setActivityUser] = useState('');
  const [activityAction, setActivityAction] = useState('');

  // ===== Users state =====
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [busyUser, setBusyUser] = useState('');
  const [tempPw, setTempPw] = useState<{ username: string; password: string; message: string } | null>(null);

  // ===== Schools state =====
  const [schools, setSchools] = useState<SchoolStatsResponse[]>([]);
  const [schoolsLoaded, setSchoolsLoaded] = useState(false);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsError, setSchoolsError] = useState('');
  const [schoolSearch, setSchoolSearch] = useState('');

  // School modal state
  const [showModal, setShowModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState<SchoolStatsResponse | null>(null);
  const [modalForm, setModalForm] = useState({ name: '', description: '', is_active: true });
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');

  // ===== Fetchers =====
  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      setStatsError('');
      setStats(await getAdminStats());
    } catch (err: any) {
      setStatsError(serverDetail(err, 'Failed to load platform stats'));
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchLoginMap = async (days: number) => {
    try {
      setMapLoading(true);
      setLoginMap(await getAdminLoginMap(days));
    } catch {
      setLoginMap(null);
    } finally {
      setMapLoading(false);
    }
  };

  const fetchActivity = async (username: string, action: string) => {
    try {
      setActivityLoading(true);
      setActivity(
        await getAdminActivity({
          limit: 200,
          username: username.trim() || undefined,
          action: action.trim() || undefined,
        })
      );
      setActivityLoaded(true);
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to load activity'));
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchUsers = async (search: string) => {
    try {
      setUsersLoading(true);
      setUsers(await getAdminUsers(search.trim()));
      setUsersLoaded(true);
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to load users'));
    } finally {
      setUsersLoading(false);
    }
  };

  const fetchSchools = async () => {
    try {
      setSchoolsLoading(true);
      setSchoolsError('');
      setSchools(await getAllSchoolsWithStats());
      setSchoolsLoaded(true);
    } catch (err: any) {
      setSchoolsError(serverDetail(err, 'Failed to load schools'));
    } finally {
      setSchoolsLoading(false);
    }
  };

  // Overview loads on mount
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Login map: on mount + when days changes
  useEffect(() => {
    fetchLoginMap(mapDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapDays]);

  // Activity: lazy load on first visit, debounce filter changes
  useEffect(() => {
    if (tab !== 'activity') return;
    const t = setTimeout(() => fetchActivity(activityUser, activityAction), activityLoaded ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, activityUser, activityAction]);

  // Users: lazy load on first visit, debounce search
  useEffect(() => {
    if (tab !== 'users') return;
    const t = setTimeout(() => fetchUsers(userSearch), usersLoaded ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, userSearch]);

  // Schools: lazy load on first visit
  useEffect(() => {
    if (tab === 'schools' && !schoolsLoaded) fetchSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ===== User actions =====
  const handleRoleChange = async (u: AdminUser, role: string) => {
    if (role === u.role) return;
    if (role === 'admin' && !window.confirm(`Make @${u.username} an admin? Admins can manage every user and school on the platform.`)) {
      return;
    }
    setBusyUser(u.username);
    try {
      await updateAdminUser(u.username, { role });
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to update role'));
    } finally {
      setBusyUser('');
      fetchUsers(userSearch);
    }
  };

  const handleActiveToggle = async (u: AdminUser) => {
    setBusyUser(u.username);
    try {
      await updateAdminUser(u.username, { active: !u.active });
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to update account status'));
    } finally {
      setBusyUser('');
      fetchUsers(userSearch);
    }
  };

  const handleResetPassword = async (u: AdminUser) => {
    if (!window.confirm(`Reset the password for @${u.username}? Their current password will stop working immediately.`)) return;
    setBusyUser(u.username);
    try {
      const res = await adminResetPassword(u.username);
      setTempPw({ username: u.username, password: res.temp_password, message: res.message });
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to reset password'));
    } finally {
      setBusyUser('');
      fetchUsers(userSearch);
    }
  };

  const handleDeleteUser = async (u: AdminUser) => {
    if (!window.confirm(`Delete @${u.username}? This removes the account permanently.`)) return;
    if (!window.confirm(`Are you absolutely sure? Deleting @${u.username} cannot be undone.`)) return;
    setBusyUser(u.username);
    try {
      await adminDeleteUser(u.username);
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to delete user'));
    } finally {
      setBusyUser('');
      fetchUsers(userSearch);
    }
  };

  // ===== School actions (unchanged behavior) =====
  const openCreateModal = () => {
    setEditingSchool(null);
    setModalForm({ name: '', description: '', is_active: true });
    setModalError('');
    setShowModal(true);
  };

  const openEditModal = (school: SchoolStatsResponse) => {
    setEditingSchool(school);
    setModalForm({ name: school.name, description: school.description, is_active: school.is_active });
    setModalError('');
    setShowModal(true);
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalForm.name.trim()) {
      setModalError('School name is required');
      return;
    }

    setModalLoading(true);
    setModalError('');

    try {
      if (editingSchool) {
        const update: SchoolUpdateRequest = {
          name: modalForm.name,
          description: modalForm.description,
          is_active: modalForm.is_active,
        };
        await updateSchool(editingSchool.school_id, update);
      } else {
        const create: SchoolCreateRequest = {
          name: modalForm.name,
          description: modalForm.description,
        };
        await createSchool(create);
      }
      setShowModal(false);
      fetchSchools();
    } catch (err: any) {
      setModalError(serverDetail(err, 'Operation failed'));
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeleteSchool = async (school: SchoolStatsResponse) => {
    if (!window.confirm(`Delete "${school.name}"? This cannot be undone.`)) return;
    try {
      await deleteSchool(school.school_id);
      fetchSchools();
    } catch (err: any) {
      alert(serverDetail(err, 'Failed to delete school'));
    }
  };

  const filteredSchools = schools.filter((s) =>
    s.name.toLowerCase().includes(schoolSearch.trim().toLowerCase())
  );

  const searchInputClass =
    'w-full rounded-full border-2 border-gray-200 bg-white px-4 py-2 text-sm text-gray-900 placeholder-gray-400 transition-colors focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 dark:border-secondary-600 dark:bg-secondary-700 dark:text-white dark:placeholder-secondary-400';

  return (
    <Layout>
      <div className="space-y-6">
        {/* === Header === */}
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white sm:text-3xl">
            Admin
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Superuser control center for the whole platform.
          </p>
        </div>

        {/* === Pill tabs === */}
        <div className="inline-flex flex-wrap gap-1 rounded-full border border-gray-200 bg-white p-1 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`rounded-full px-4 py-1.5 text-sm font-bold transition-all ${
                tab === t.key
                  ? 'bg-primary-600 text-gray-900 shadow-[0_2px_0_0_rgba(0,0,0,0.12)]'
                  : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ================= OVERVIEW ================= */}
        {tab === 'overview' && (
          <div className="space-y-6">
            {statsLoading ? (
              <Spinner />
            ) : statsError ? (
              <div className="rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">
                {statsError}
              </div>
            ) : stats ? (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <StatCard
                  label="Students"
                  value={stats.users['student'] || 0}
                  tint="bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 8l10 5 10-5-10-5z" /><path d="M6 10.5V15c0 1.5 2.7 3 6 3s6-1.5 6-3v-4.5" /></svg>}
                />
                <StatCard
                  label="Teachers"
                  value={stats.users['teacher'] || 0}
                  tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.2" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>}
                />
                <StatCard
                  label="Admins"
                  value={stats.users['admin'] || 0}
                  tint="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l7 3v5c0 4.5-3 8.5-7 10-4-1.5-7-5.5-7-10V6l7-3z" /></svg>}
                />
                <StatCard
                  label="Courses"
                  value={stats.courses}
                  tint="bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h11a2 2 0 012 2v12a2 2 0 00-2-2H4V5z" /><path d="M20 5H9" /></svg>}
                />
                <StatCard
                  label="Lectures"
                  value={stats.lectures}
                  tint="bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="12" rx="2" /><path d="M8 20h8M12 16v4" /></svg>}
                />
                <StatCard
                  label="Quizzes"
                  value={stats.quizzes}
                  sub={`${stats.quiz_attempts.toLocaleString()} attempts`}
                  tint="bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4" /><rect x="3" y="4" width="18" height="16" rx="2" /></svg>}
                />
                <StatCard
                  label="Assignments"
                  value={stats.assignments}
                  sub={`${stats.assignment_submissions.toLocaleString()} submissions`}
                  tint="bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>}
                />
                <StatCard
                  label="Reflections"
                  value={stats.reflections}
                  tint="bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a8 8 0 11-4-6.9" /><path d="M21 5l-9 9-2-2" /></svg>}
                />
                <StatCard
                  label="Logins 24h"
                  value={stats.logins_24h}
                  sub={`${stats.logins_7d.toLocaleString()} in 7 days`}
                  tint="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 012 2v14a2 2 0 01-2 2h-4" /><path d="M10 17l5-5-5-5M15 12H3" /></svg>}
                />
                <StatCard
                  label="Activity 24h"
                  value={stats.activity_24h}
                  tint="bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
                  icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg>}
                />
              </div>
            ) : null}

            {/* World login map */}
            <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-base font-extrabold tracking-tight text-gray-900 dark:text-white">
                  Logins around the world
                </h2>
                <div className="inline-flex self-start rounded-full border border-gray-200 bg-gray-50 p-0.5 dark:border-secondary-600 dark:bg-secondary-700">
                  {[7, 30, 90].map((d) => (
                    <button
                      key={d}
                      onClick={() => setMapDays(d)}
                      className={`rounded-full px-3 py-1 text-xs font-bold transition-all ${
                        mapDays === d
                          ? 'bg-primary-600 text-gray-900 shadow-[0_2px_0_0_rgba(0,0,0,0.12)]'
                          : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'
                      }`}
                    >
                      {d}d
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative mt-4 overflow-hidden rounded-xl">
                <img
                  src="/world-map.jpg"
                  alt="World map of login locations"
                  className="w-full rounded-xl opacity-80 brightness-95 dark:opacity-60"
                  style={{ aspectRatio: '2 / 1', objectFit: 'cover' }}
                />
                {mapLoading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-white/40 dark:bg-secondary-900/40">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600" />
                  </div>
                )}
                {!mapLoading &&
                  loginMap?.points.map((p, i) => {
                    const size = p.count >= 20 ? 'h-4 w-4' : p.count >= 5 ? 'h-3 w-3' : 'h-2 w-2';
                    return (
                      <span
                        key={`${p.lat}-${p.lon}-${i}`}
                        title={`${p.count} logins · ${p.city}, ${p.country} · ${p.usernames.slice(0, 3).join(', ')}`}
                        className="absolute -translate-x-1/2 -translate-y-1/2 cursor-default"
                        style={{
                          left: `${((p.lon + 180) / 360) * 100}%`,
                          top: `${((90 - p.lat) / 180) * 100}%`,
                        }}
                      >
                        <span className={`absolute inline-flex ${size} animate-ping rounded-full bg-primary-400 opacity-60`} />
                        <span className={`relative inline-flex ${size} rounded-full bg-primary-400 ring-2 ring-white/80 dark:ring-secondary-900/60`} />
                      </span>
                    );
                  })}
                {!mapLoading && loginMap && loginMap.points.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="rounded-full bg-white/80 px-4 py-1.5 text-xs font-semibold text-gray-500 dark:bg-secondary-900/70 dark:text-gray-400">
                      No geolocated logins in this window
                    </span>
                  </div>
                )}
              </div>

              {loginMap && (
                <p className="mt-3 text-xs font-medium text-gray-500 dark:text-gray-400">
                  {loginMap.total.toLocaleString()} logins in the last {loginMap.days} days · {loginMap.local_or_unknown.toLocaleString()} from local or unknown networks
                </p>
              )}

              {loginMap && loginMap.recent.length > 0 && (
                <div className="mt-4 border-t border-gray-100 pt-4 dark:border-secondary-700">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                    Recent logins
                  </p>
                  <ul className="mt-2 space-y-1.5">
                    {loginMap.recent.slice(0, 10).map((r, i) => (
                      <li key={`${r.username}-${r.at}-${i}`} className="flex items-center gap-2.5 text-sm">
                        <span className={`h-2 w-2 shrink-0 rounded-full ${r.success ? 'bg-emerald-500' : 'bg-red-500'}`} />
                        <span className="font-bold text-gray-900 dark:text-white">{r.username}</span>
                        <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                          {[r.city, r.country].filter(Boolean).join(', ') || r.ip || 'unknown location'}
                        </span>
                        <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-gray-400">{timeAgo(r.at)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ================= ACTIVITY ================= */}
        {tab === 'activity' && (
          <div className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="text"
                value={activityUser}
                onChange={(e) => setActivityUser(e.target.value)}
                placeholder="Filter by username"
                className={`${searchInputClass} sm:max-w-xs`}
              />
              <input
                type="text"
                value={activityAction}
                onChange={(e) => setActivityAction(e.target.value)}
                placeholder="Filter by action, e.g. auth.login or POST /api"
                className={`${searchInputClass} sm:max-w-sm`}
              />
            </div>

            {activityLoading && !activityLoaded ? (
              <Spinner />
            ) : activity.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                <Mascot name="clock" chip="#93c5fd" className="h-16 w-16 shadow-medium animate-float-slow" blinkDelay={0.5} />
                <h3 className="mt-6 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No activity found</h3>
                <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                  Nothing matches these filters yet. Try a different username or action.
                </p>
              </div>
            ) : (
              <div className={`overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800 ${activityLoading ? 'opacity-60' : ''}`}>
                <ul className="divide-y divide-gray-100 dark:divide-secondary-700">
                  {activity.map((ev) => {
                    const ok = ev.status === 'ok';
                    return (
                      <li key={ev.event_id} className="flex items-center gap-3 px-4 py-2.5">
                        <span
                          title={ev.status}
                          className={`h-2 w-2 shrink-0 rounded-full ${ok ? 'bg-emerald-500' : 'bg-red-500'}`}
                        />
                        <span className="w-28 shrink-0 truncate text-sm font-bold text-gray-900 dark:text-white sm:w-36">
                          {ev.username}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate font-mono text-xs text-gray-700 dark:text-gray-300">{ev.action}</span>
                          {ev.detail && (
                            <span className="block truncate text-xs text-gray-500 dark:text-gray-400">{ev.detail}</span>
                          )}
                        </span>
                        <span className="hidden shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400 md:inline">
                          {ev.ip}
                        </span>
                        <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">
                          {new Date(ev.at).toLocaleString()}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* ================= USERS ================= */}
        {tab === 'users' && (
          <div className="space-y-4">
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search users by name, username, or email"
              className={`${searchInputClass} sm:max-w-md`}
            />

            {usersLoading && !usersLoaded ? (
              <Spinner />
            ) : users.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No users match this search.</p>
              </div>
            ) : (
              <div className={`overflow-x-auto rounded-2xl border border-gray-200 bg-white shadow-soft dark:border-secondary-700 dark:bg-secondary-800 ${usersLoading ? 'opacity-60' : ''}`}>
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 dark:border-secondary-700">
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">User</th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Email</th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                      <th className="px-4 py-3 text-[11px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Last login</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-secondary-700">
                    {users.map((u) => {
                      const busy = busyUser === u.username;
                      return (
                        <tr key={u.username} className={`group ${busy ? 'opacity-50' : ''}`}>
                          <td className="px-4 py-3">
                            <p className="font-bold text-gray-900 dark:text-white">{u.full_name || u.username}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">@{u.username}</p>
                          </td>
                          <td className="max-w-[200px] truncate px-4 py-3 text-gray-600 dark:text-gray-300">{u.email}</td>
                          <td className="px-4 py-3">
                            <select
                              value={u.role}
                              disabled={busy}
                              onChange={(e) => handleRoleChange(u, e.target.value)}
                              className="rounded-full border-2 border-gray-200 bg-white px-2.5 py-1 text-xs font-bold text-gray-700 focus:border-primary-500 focus:outline-none disabled:opacity-50 dark:border-secondary-600 dark:bg-secondary-700 dark:text-gray-200"
                            >
                              <option value="student">student</option>
                              <option value="teacher">teacher</option>
                              <option value="admin">admin</option>
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => handleActiveToggle(u)}
                              disabled={busy}
                              title={u.active ? 'Click to deactivate' : 'Click to activate'}
                              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold transition-colors disabled:opacity-50 ${
                                u.active
                                  ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/25 dark:text-emerald-300 dark:hover:bg-emerald-900/40'
                                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-secondary-700 dark:text-gray-400 dark:hover:bg-secondary-600'
                              }`}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${u.active ? 'bg-emerald-500' : 'bg-gray-400'}`} />
                              {u.active ? 'Active' : 'Deactivated'}
                            </button>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                            {timeAgo(u.last_login)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                              <button
                                onClick={() => handleResetPassword(u)}
                                disabled={busy}
                                className="whitespace-nowrap rounded-full border-2 border-gray-200 px-3 py-1 text-xs font-bold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700"
                              >
                                Reset password
                              </button>
                              <button
                                onClick={() => handleDeleteUser(u)}
                                disabled={busy}
                                title="Delete user"
                                className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                              >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ================= SCHOOLS ================= */}
        {tab === 'schools' && (
          <div className="space-y-6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <input
                type="text"
                value={schoolSearch}
                onChange={(e) => setSchoolSearch(e.target.value)}
                placeholder="Search schools by name"
                className={`${searchInputClass} sm:max-w-xs`}
              />
              <button
                onClick={openCreateModal}
                className="inline-flex shrink-0 items-center gap-2 self-start rounded-full bg-primary-600 px-5 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none sm:self-auto"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                </svg>
                Add school
              </button>
            </div>

            {schoolsLoading ? (
              <Spinner />
            ) : schoolsError ? (
              <div className="flex items-center gap-3 rounded-2xl border-l-4 border-red-500 bg-red-50 px-4 py-3 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M12 3a9 9 0 100 18 9 9 0 000-18z" />
                </svg>
                {schoolsError}
              </div>
            ) : schools.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 bg-white/60 px-6 py-16 text-center dark:border-secondary-700 dark:bg-secondary-800/40">
                <Mascot name="folder" chip="#93c5fd" className="h-16 w-16 shadow-medium animate-float-slow" blinkDelay={0.5} />
                <h3 className="mt-6 text-lg font-extrabold tracking-tight text-gray-900 dark:text-white">No schools yet</h3>
                <p className="mt-1.5 max-w-sm text-sm text-gray-500 dark:text-gray-400">
                  Create your first school to start managing the platform.
                </p>
                <button
                  onClick={openCreateModal}
                  className="mt-6 rounded-full bg-primary-600 px-6 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                >
                  Create your first school
                </button>
              </div>
            ) : filteredSchools.length === 0 ? (
              <div className="rounded-2xl border border-gray-200 bg-white px-6 py-12 text-center shadow-soft dark:border-secondary-700 dark:bg-secondary-800">
                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No schools match "{schoolSearch}".</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                {filteredSchools.map((school) => (
                  <div
                    key={school.school_id}
                    className="group flex flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:border-primary-300 hover:shadow-medium dark:border-secondary-700 dark:bg-secondary-800 dark:hover:border-primary-800"
                  >
                    {/* Header: school glyph + hover-reveal actions */}
                    <div className="flex items-start justify-between">
                      <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round">
                          <path d="M3 21h18M5 21V8l7-4 7 4v13M9 21v-5a1 1 0 011-1h4a1 1 0 011 1v5M9 9h.01M15 9h.01M9 13h.01M15 13h.01" />
                        </svg>
                      </span>
                      <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
                        <button
                          onClick={() => openEditModal(school)}
                          title="Edit school"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-secondary-700 dark:hover:text-gray-200"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                        </button>
                        <button
                          onClick={() => handleDeleteSchool(school)}
                          title="Delete school"
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                        >
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                        </button>
                      </div>
                    </div>

                    {/* Title + description */}
                    <h3 className="mt-4 line-clamp-2 text-base font-bold leading-snug text-gray-900 dark:text-white">{school.name}</h3>
                    <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-gray-500 dark:text-gray-400">
                      {school.description || 'No description provided.'}
                    </p>

                    {/* Meta rows */}
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="3.2" /><path d="M5.5 20a6.5 6.5 0 0 1 13 0" /></svg>
                        {school.teacher_count} teachers
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 2 8l10 5 10-5-10-5z" /><path d="M6 10.5V15c0 1.5 2.7 3 6 3s6-1.5 6-3v-4.5" /></svg>
                        {school.student_count} students
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 5h11a2 2 0 012 2v12a2 2 0 00-2-2H4V5z" /><path d="M20 5H9" /></svg>
                        {school.course_count} courses
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4" /><rect x="3" y="4" width="18" height="16" rx="2" /></svg>
                        {school.enrollment_count} enrolled
                      </span>
                    </div>

                    {/* Footer: status + CTA */}
                    <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4 dark:border-secondary-700">
                      {school.is_active ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/25 dark:text-emerald-300">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-500 dark:bg-secondary-700 dark:text-gray-400">
                          <span className="h-1.5 w-1.5 rounded-full bg-gray-400" />
                          Inactive
                        </span>
                      )}
                      <button
                        onClick={() => openEditModal(school)}
                        className="inline-flex items-center gap-1 rounded-full bg-primary-600 px-4 py-2 text-xs font-bold text-gray-900 shadow-[0_3px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
                      >
                        Manage
                        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Temp password modal */}
      {tempPw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-3xl border border-gray-200 bg-white p-8 shadow-large dark:border-secondary-700 dark:bg-secondary-800">
            <h2 className="text-xl font-extrabold tracking-tight text-gray-900 dark:text-white">
              Password reset for @{tempPw.username}
            </h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {tempPw.message || 'Share this temporary password with the user. It is shown only once.'}
            </p>
            <div className="mt-4 rounded-2xl border-2 border-dashed border-primary-300 bg-primary-50 px-4 py-3 text-center dark:border-primary-800 dark:bg-primary-900/20">
              <code className="select-all font-mono text-lg font-bold tracking-wide text-gray-900 dark:text-white">
                {tempPw.password}
              </code>
            </div>
            <p className="mt-3 text-xs font-semibold text-amber-600 dark:text-amber-400">
              Copy it now. You will not be able to see this password again.
            </p>
            <button
              onClick={() => setTempPw(null)}
              className="mt-6 w-full rounded-full bg-primary-600 px-4 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none"
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* Create/Edit School Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-secondary-800 rounded-3xl shadow-large border border-gray-200 dark:border-secondary-700 p-8 w-full max-w-md mx-4">
            <h2 className="text-xl font-extrabold tracking-tight text-gray-900 dark:text-white mb-6">
              {editingSchool ? 'Edit School' : 'Create School'}
            </h2>

            <form onSubmit={handleModalSubmit} className="space-y-4">
              {modalError && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl text-sm">
                  {modalError}
                </div>
              )}

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                  School Name
                </label>
                <input
                  type="text"
                  value={modalForm.name}
                  onChange={(e) => setModalForm({ ...modalForm, name: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500"
                  placeholder="Enter school name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                  Description
                </label>
                <textarea
                  value={modalForm.description}
                  onChange={(e) => setModalForm({ ...modalForm, description: e.target.value })}
                  className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-secondary-600 rounded-2xl bg-white dark:bg-secondary-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-secondary-400 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 resize-none"
                  rows={3}
                  placeholder="Optional description"
                />
              </div>

              {editingSchool && (
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={modalForm.is_active}
                    onChange={(e) => setModalForm({ ...modalForm, is_active: e.target.checked })}
                    className="w-4 h-4 text-primary-600 rounded border-gray-300 dark:border-secondary-600 focus:ring-primary-500"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                    Active (visible in registration)
                  </label>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 rounded-full border-2 border-gray-200 px-4 py-2.5 text-sm font-bold text-gray-700 transition-colors hover:bg-gray-50 dark:border-secondary-600 dark:text-gray-300 dark:hover:bg-secondary-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={modalLoading}
                  className="flex-1 rounded-full bg-primary-600 px-4 py-2.5 text-sm font-bold text-gray-900 shadow-[0_4px_0_0_rgba(0,0,0,0.12)] transition-all hover:bg-primary-700 active:translate-y-0.5 active:shadow-none disabled:opacity-50 disabled:shadow-none"
                >
                  {modalLoading ? 'Saving...' : editingSchool ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminDashboard;
